<script setup>
/**
 * This component handles parsing and sending WhatsApp message templates.
 * It works as follows:
 * 1. Displays the template text with variable placeholders.
 * 2. Generates input fields for each variable in the template.
 * 3. Validates that all variables are filled before sending.
 * 4. Replaces placeholders with user-provided values.
 * 5. Emits events to send the processed message or reset the template.
 */
import { ref, computed, onMounted, watch } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { requiredIf } from '@vuelidate/validators';
import { useI18n } from 'vue-i18n';

import { isWhatsAppComplete } from '@chatwoot/utils';
import Input from 'dashboard/components-next/input/Input.vue';
import { uploadFile } from 'dashboard/helper/uploadHelper';
import {
  buildTemplateParameters,
  allKeysRequired,
  replaceTemplateVariables,
  DEFAULT_LANGUAGE,
  DEFAULT_CATEGORY,
  COMPONENT_TYPES,
  MEDIA_FORMATS,
  findComponentByType,
} from 'dashboard/helper/templateHelper';

const MEDIA_SIZE_LIMITS = {
  IMAGE: 5 * 1024 * 1024,
  VIDEO: 16 * 1024 * 1024,
  DOCUMENT: 100 * 1024 * 1024,
};

const ACCEPTED_TYPES = {
  IMAGE: 'image/*',
  VIDEO: 'video/*',
  DOCUMENT: '*/*',
};

const props = defineProps({
  template: {
    type: Object,
    default: () => ({}),
    validator: value => {
      if (!value || typeof value !== 'object') return false;
      if (!value.components || !Array.isArray(value.components)) return false;
      return true;
    },
  },
});

const emit = defineEmits(['sendMessage', 'resetTemplate', 'back']);

const { t } = useI18n();

const processedParams = ref({});
const mediaFileSize = ref(null);
const mediaCheckLoading = ref(false);
const mediaInputMode = ref('url'); // 'url' | 'file'
const uploadLoading = ref(false);
const uploadError = ref('');
const uploadedFileName = ref('');

const languageLabel = computed(() => {
  return `${t('WHATSAPP_TEMPLATES.PARSER.LANGUAGE')}: ${props.template.language || DEFAULT_LANGUAGE}`;
});

const categoryLabel = computed(() => {
  return `${t('WHATSAPP_TEMPLATES.PARSER.CATEGORY')}: ${props.template.category || DEFAULT_CATEGORY}`;
});

const headerComponent = computed(() => {
  return findComponentByType(props.template, COMPONENT_TYPES.HEADER);
});

const bodyComponent = computed(() => {
  return findComponentByType(props.template, COMPONENT_TYPES.BODY);
});

const bodyText = computed(() => {
  return bodyComponent.value?.text || '';
});

const hasMediaHeader = computed(() =>
  MEDIA_FORMATS.includes(headerComponent.value?.format)
);

const formatType = computed(() => {
  const format = headerComponent.value?.format;
  return format ? format.charAt(0) + format.slice(1).toLowerCase() : '';
});

const isDocumentTemplate = computed(() => {
  return headerComponent.value?.format?.toLowerCase() === 'document';
});

const hasVariables = computed(() => {
  return bodyText.value?.match(/{{([^}]+)}}/g) !== null;
});

const renderedTemplate = computed(() => {
  return replaceTemplateVariables(bodyText.value, processedParams.value);
});

const mediaFormatUpperCase = computed(
  () => headerComponent.value?.format?.toUpperCase() || 'IMAGE'
);

const mediaSizeLimit = computed(
  () => MEDIA_SIZE_LIMITS[mediaFormatUpperCase.value] ?? MEDIA_SIZE_LIMITS.IMAGE
);

const acceptedFileTypes = computed(
  () => ACCEPTED_TYPES[mediaFormatUpperCase.value] ?? '*/*'
);

const isMediaTooLarge = computed(
  () => mediaFileSize.value !== null && mediaFileSize.value > mediaSizeLimit.value
);

const formatBytes = bytes => {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const isFormInvalid = computed(() => {
  if (!hasVariables.value && !hasMediaHeader.value) return false;

  if (hasMediaHeader.value && !processedParams.value.header?.media_url) {
    return true;
  }

  if (isMediaTooLarge.value) return true;

  if (uploadLoading.value) return true;

  if (hasVariables.value && processedParams.value.body) {
    const hasEmptyBodyVariable = Object.values(processedParams.value.body).some(
      value => !value
    );
    if (hasEmptyBodyVariable) return true;
  }

  if (processedParams.value.buttons) {
    const hasEmptyButtonParameter = processedParams.value.buttons.some(
      button => !button.parameter
    );
    if (hasEmptyButtonParameter) return true;
  }

  return false;
});

const v$ = useVuelidate(
  {
    processedParams: {
      requiredIfKeysPresent: requiredIf(hasVariables),
      allKeysRequired,
    },
  },
  { processedParams }
);

const CONTACT_NAME_TOKEN = 'contact.name';

const initializeTemplateParameters = () => {
  processedParams.value = buildTemplateParameters(
    props.template,
    hasMediaHeader.value
  );
  // Pré-preenche a primeira variável do body com contact.name
  if (processedParams.value.body) {
    const firstKey = Object.keys(processedParams.value.body)[0];
    if (firstKey !== undefined && processedParams.value.body[firstKey] === '') {
      processedParams.value.body[firstKey] = CONTACT_NAME_TOKEN;
    }
  }
};

const resetMediaState = () => {
  mediaFileSize.value = null;
  uploadError.value = '';
  uploadedFileName.value = '';
};

const switchMode = mode => {
  mediaInputMode.value = mode;
  processedParams.value.header ??= {};
  processedParams.value.header.media_url = '';
  resetMediaState();
};

const updateMediaUrl = async value => {
  processedParams.value.header ??= {};
  processedParams.value.header.media_url = value;
  mediaFileSize.value = null;

  if (!value) return;

  try {
    mediaCheckLoading.value = true;
    const res = await fetch(value, { method: 'HEAD' });
    const contentLength = res.headers.get('content-length');
    const parsed = contentLength ? parseInt(contentLength, 10) : 0;
    if (parsed > 0) mediaFileSize.value = parsed;
  } catch {
    // CORS ou URL inválida — ignora silenciosamente
  } finally {
    mediaCheckLoading.value = false;
  }
};

const handleFileSelect = async event => {
  const file = event.target.files?.[0];
  if (!file) return;

  uploadError.value = '';
  uploadedFileName.value = '';
  mediaFileSize.value = file.size;

  if (file.size > mediaSizeLimit.value) {
    return;
  }

  try {
    uploadLoading.value = true;
    const { fileUrl } = await uploadFile(file);
    processedParams.value.header ??= {};
    processedParams.value.header.media_url = fileUrl;
    uploadedFileName.value = file.name;
  } catch {
    uploadError.value = 'Erro ao fazer upload. Tente novamente.';
    mediaFileSize.value = null;
  } finally {
    uploadLoading.value = false;
  }

  event.target.value = '';
};

const updateMediaName = value => {
  processedParams.value.header ??= {};
  processedParams.value.header.media_name = value;
};

const sendMessage = () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  const { name, category, language, namespace } = props.template;

  const payload = {
    message: renderedTemplate.value,
    templateParams: {
      name,
      category,
      language,
      namespace,
      processed_params: processedParams.value,
    },
  };
  emit('sendMessage', payload);
};

const resetTemplate = () => {
  emit('resetTemplate');
};

const goBack = () => {
  emit('back');
};

onMounted(initializeTemplateParameters);

watch(
  () => props.template,
  () => {
    initializeTemplateParameters();
    v$.value.$reset();
    resetMediaState();
    mediaInputMode.value = 'url';
  },
  { deep: true }
);

defineExpose({
  processedParams,
  hasVariables,
  hasMediaHeader,
  isDocumentTemplate,
  headerComponent,
  renderedTemplate,
  v$,
  updateMediaUrl,
  updateMediaName,
  sendMessage,
  resetTemplate,
  goBack,
});
</script>

<template>
  <div>
    <div class="flex flex-col gap-4 p-4 mb-4 rounded-lg bg-n-alpha-black2">
      <div class="flex justify-between items-center">
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ template.name }}
        </h3>
        <span class="text-xs text-n-slate-11">
          {{ languageLabel }}
        </span>
      </div>

      <div class="flex flex-col gap-2">
        <div class="rounded-md">
          <div class="text-sm whitespace-pre-wrap text-n-slate-12">
            {{ renderedTemplate }}
          </div>
        </div>
      </div>

      <div class="text-xs text-n-slate-11">
        {{ categoryLabel }}
      </div>
    </div>

    <div v-if="hasVariables || hasMediaHeader">
      <div v-if="hasMediaHeader" class="mb-4">
        <p class="mb-2.5 text-sm font-semibold">
          {{
            $t('WHATSAPP_TEMPLATES.PARSER.MEDIA_HEADER_LABEL', {
              type: formatType,
            }) || `${formatType} Header`
          }}
        </p>

        <!-- Toggle URL / Arquivo -->
        <div class="flex mb-3 border border-n-weak rounded-lg overflow-hidden">
          <button
            type="button"
            class="flex-1 px-3 py-1.5 text-xs font-medium transition-colors"
            :class="
              mediaInputMode === 'url'
                ? 'bg-n-brand text-white'
                : 'bg-transparent text-n-slate-11 hover:bg-n-alpha-1'
            "
            @click="switchMode('url')"
          >
            URL
          </button>
          <button
            type="button"
            class="flex-1 px-3 py-1.5 text-xs font-medium transition-colors"
            :class="
              mediaInputMode === 'file'
                ? 'bg-n-brand text-white'
                : 'bg-transparent text-n-slate-11 hover:bg-n-alpha-1'
            "
            @click="switchMode('file')"
          >
            Meu computador
          </button>
        </div>

        <!-- Modo URL -->
        <div v-if="mediaInputMode === 'url'">
          <div class="flex items-center mb-2.5">
            <Input
              :model-value="processedParams.header?.media_url || ''"
              type="url"
              class="flex-1"
              :placeholder="
                t('WHATSAPP_TEMPLATES.PARSER.MEDIA_URL_LABEL', {
                  type: formatType,
                })
              "
              @update:model-value="updateMediaUrl"
            />
          </div>
          <div v-if="mediaCheckLoading" class="mt-1 text-xs text-n-slate-11">
            Verificando tamanho...
          </div>
          <div
            v-else-if="isMediaTooLarge"
            class="mt-1 p-2 rounded-md bg-n-ruby-9/20 text-n-ruby-9 text-xs"
          >
            Arquivo muito grande: {{ formatBytes(mediaFileSize) }}. O WhatsApp
            aceita no máximo {{ formatBytes(mediaSizeLimit) }} para
            {{ formatType }}. Comprima o arquivo e atualize a URL.
          </div>
          <div
            v-else-if="mediaFileSize !== null"
            class="mt-1 text-xs text-n-slate-11"
          >
            Tamanho: {{ formatBytes(mediaFileSize) }} ✓
          </div>
        </div>

        <!-- Modo Arquivo -->
        <div v-else>
          <label
            class="flex flex-col items-center justify-center w-full h-24 border-2 border-dashed border-n-weak rounded-lg cursor-pointer hover:bg-n-alpha-1 transition-colors"
            :class="{ 'opacity-60 pointer-events-none': uploadLoading }"
          >
            <div v-if="uploadLoading" class="text-xs text-n-slate-11">
              Fazendo upload...
            </div>
            <div
              v-else-if="uploadedFileName"
              class="flex flex-col items-center gap-1"
            >
              <span class="text-xs font-medium text-n-slate-12">
                {{ uploadedFileName }}
              </span>
              <span class="text-xs text-n-slate-11">
                {{ formatBytes(mediaFileSize) }} ✓
              </span>
            </div>
            <div
              v-else-if="isMediaTooLarge"
              class="flex flex-col items-center gap-1 px-4 text-center"
            >
              <span class="text-xs text-n-ruby-9 font-medium">
                Arquivo muito grande: {{ formatBytes(mediaFileSize) }}
              </span>
              <span class="text-xs text-n-slate-11">
                Máximo: {{ formatBytes(mediaSizeLimit) }} para {{ formatType }}
              </span>
            </div>
            <div v-else class="flex flex-col items-center gap-1">
              <span class="text-xs font-medium text-n-slate-12">
                Clique para selecionar {{ formatType }}
              </span>
              <span class="text-xs text-n-slate-11">
                Máx. {{ formatBytes(mediaSizeLimit) }}
              </span>
            </div>
            <input
              type="file"
              class="hidden"
              :accept="acceptedFileTypes"
              @change="handleFileSelect"
            />
          </label>
          <div
            v-if="uploadError"
            class="mt-1 p-2 rounded-md bg-n-ruby-9/20 text-n-ruby-9 text-xs"
          >
            {{ uploadError }}
          </div>
        </div>
      </div>

      <div v-if="isDocumentTemplate && hasMediaHeader" class="flex items-center mb-2.5">
        <Input
          :model-value="processedParams.header?.media_name || ''"
          type="text"
          class="flex-1"
          :placeholder="
            t('WHATSAPP_TEMPLATES.PARSER.DOCUMENT_NAME_PLACEHOLDER')
          "
          @update:model-value="updateMediaName"
        />
      </div>

      <!-- Body Variables Section -->
      <div v-if="processedParams.body">
        <p class="mb-2.5 text-sm font-semibold">
          {{ $t('WHATSAPP_TEMPLATES.PARSER.VARIABLES_LABEL') }}
        </p>
        <div
          v-for="(variable, key) in processedParams.body"
          :key="`body-${key}`"
          class="mb-3"
        >
          <Input
            v-model="processedParams.body[key]"
            type="text"
            class="flex-1 w-full"
            :placeholder="
              t('WHATSAPP_TEMPLATES.PARSER.VARIABLE_PLACEHOLDER', {
                variable: key,
              })
            "
          />
          <div class="flex items-center gap-1.5 mt-1.5">
            <span class="text-[11px] text-n-slate-10">Sugestão:</span>
            <button
              type="button"
              class="text-[11px] px-2 py-0.5 rounded-full border transition-colors"
              :class="processedParams.body[key] === CONTACT_NAME_TOKEN
                ? 'bg-n-brand/10 border-n-brand text-n-brand'
                : 'bg-n-alpha-1 border-n-weak text-n-slate-11 hover:bg-n-brand/10 hover:border-n-brand hover:text-n-brand'"
              @click="processedParams.body[key] = CONTACT_NAME_TOKEN"
            >
              contact.name
            </button>
          </div>
        </div>
      </div>

      <!-- Button Variables Section -->
      <div v-if="processedParams.buttons">
        <p class="mb-2.5 text-sm font-semibold">
          {{ t('WHATSAPP_TEMPLATES.PARSER.BUTTON_PARAMETERS') }}
        </p>
        <div
          v-for="(button, index) in processedParams.buttons"
          :key="`button-${index}`"
          class="flex items-center mb-2.5"
        >
          <Input
            v-model="processedParams.buttons[index].parameter"
            type="text"
            class="flex-1"
            :placeholder="t('WHATSAPP_TEMPLATES.PARSER.BUTTON_PARAMETER')"
          />
        </div>
      </div>
      <p
        v-if="v$.$dirty && v$.$invalid"
        class="p-2.5 text-center rounded-md bg-n-ruby-9/20 text-n-ruby-9"
      >
        {{ $t('WHATSAPP_TEMPLATES.PARSER.FORM_ERROR_MESSAGE') }}
      </p>
    </div>

    <slot
      name="actions"
      :send-message="sendMessage"
      :reset-template="resetTemplate"
      :go-back="goBack"
      :is-valid="!v$.$invalid"
      :disabled="isFormInvalid"
    />
  </div>
</template>
